"use client";

import { Calendar, TrendingUp, TrendingDown } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  type TooltipContentProps,
  Tooltip,
  Cell,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BillingService } from "@/services/BillingService";
import { useEffect, useState } from "react";
import type { BillingsSalesDailyOutput } from "@/interfaces/billing";

import { numberToMoney } from "@/util/moneyConversion";
import { Skeleton } from "@/components/ui/skeleton";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { BaseComponentProps, ChartDataItem } from "@/interfaces/dashboard";
import { FinancialColors } from "@/util/chartColors";

export function DailySalesChart({
  monthYear,
  isLoading = false,
}: BaseComponentProps) {
  const [dailySales, setDailySales] = useState<BillingsSalesDailyOutput[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [averageSales, setAverageSales] = useState<number>(0);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const service = new BillingService();
      const response = await service.getSalesDaily({
        monthYear,
      });

      setDailySales(response);

      // Calcular média de vendas diárias
      if (response.length > 0) {
        const total = response.reduce((sum, item) => sum + item.totalAmount, 0);
        setAverageSales(total / response.length);
      }
    } catch {
      setError("Falha ao carregar dados de vendas diárias");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [monthYear]);

  // Transform the data for the chart
  const chartData: ChartDataItem[] = dailySales.map((sale) => {
    const date = new Date(
      Number(monthYear.split("/")[1]),
      Number(monthYear.split("/")[0]) - 1,
      sale.day
    );
    return {
      day: `${sale.day}`,
      totalAmount: sale.totalAmount,
      formattedDate: formatDayToDate(sale.day, monthYear),
      dayOfWeek: date.toLocaleDateString("pt-BR", { weekday: "short" }),
    };
  });

  function formatDayToDate(day: number, monthYear: string): string {
    const [month, year] = monthYear.split("/");
    const date = new Date(Number(year), Number(month) - 1, day);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  const CustomTooltip = ({ active, payload }: TooltipContentProps<number, string>) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ChartDataItem;
      const isAboveAverage = data.totalAmount > averageSales;

      return (
        <div className="bg-white p-4 border rounded shadow">
          <p className="font-bold mb-1">
            {data.formattedDate} ({data.dayOfWeek})
          </p>
          <p>Total de vendas: {numberToMoney(data.totalAmount)}</p>
          <div className="flex items-center mt-1 text-sm">
            <span className="mr-1">Média:</span>
            <Badge
              variant={isAboveAverage ? "success" : "destructive"}
              className="flex items-center text-xs"
            >
              {isAboveAverage ? (
                <TrendingUp className="h-3 w-3 mr-1" />
              ) : (
                <TrendingDown className="h-3 w-3 mr-1" />
              )}
              {isAboveAverage ? "Acima" : "Abaixo"} da média
            </Badge>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <CardTitle className="flex items-center">
          <Calendar className="h-5 w-5 mr-2 text-orange-500" />
          Vendas Diárias
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading || loading ? (
          <div className="flex justify-center items-center h-64">
            <Skeleton className="h-[300px] w-full rounded-md" />
          </div>
        ) : error ? (
          <div className="flex justify-center items-center h-64 text-red-500">
            {error}
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex justify-center items-center h-64 text-muted-foreground">
            Nenhum dado disponível para este período
          </div>
        ) : (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <div>
                <span className="text-sm text-muted-foreground">
                  Média diária:
                </span>
                <span className="ml-2 font-semibold">
                  {numberToMoney(averageSales)}
                </span>
              </div>
              <Badge variant="outline" className="bg-orange-50">
                {chartData.length} dias com vendas
              </Badge>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="day"
                    tickLine={false}
                    tickMargin={10}
                    axisLine={false}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => numberToMoney(value)}
                    width={80}
                  />
                  <Tooltip content={CustomTooltip} />
                  <Bar
                    dataKey="totalAmount"
                    name="Vendas"
                    fill={FinancialColors.income}
                    radius={[4, 4, 0, 0]}
                    animationDuration={1500}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          entry.totalAmount > averageSales
                            ? FinancialColors.income
                            : FinancialColors.neutral
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </motion.div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
